import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { CompanyDetailPanelsComponent } from '../../components/company-detail-panels/company-detail-panels.component';
import { CompanyFormComponent } from '../../components/company-form/company-form.component';
import { DivisionModalComponent } from '../../components/division-modal/division-modal.component';
import { OrganisationalLevelModalComponent } from '../../components/organisational-level-modal/organisational-level-modal.component';

import { Permission } from '@shared/enums/permission.enum';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';

import { PermissionService } from '@core/services/permission.service';
import { CompanyStore } from '../../company.store';
import {SHARED_IMPORTS} from "../../../../shared/modules/shared-imports.module";
import { SHARED_UTILS } from '@shared/modules/shared-utils.module';
import { CompanyDetailsActions } from '../../components/company-detail-panels/company-details-actions-enum';

import {
  CompanyNamedLookupRequest,
  CompanyNamedLookupResponse,
  CompanyOrganisationalLevelResponse,
  CompanyResponse,
  CompanyRequest,
  CreateCompanyOrganisationalLevelRequest,
} from '../../company.model';

import {DetailsPageLayoutComponent} from '@core/layout/page-layouts/details-page-layout/details-page-layout-component';

@Component({
  selector: 'app-company-detail-page',
  imports: [
    CompanyDetailPanelsComponent,
    CompanyFormComponent,
    DetailsPageLayoutComponent,
    ...SharedMaterialUIModule,
    ...SHARED_IMPORTS,
  ],
  templateUrl: './company-detail.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CompanyDetailPage {
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly permissionService = inject(PermissionService);

  protected readonly companyStore = inject(CompanyStore);
  readonly utils = SHARED_UTILS();
  protected readonly companyId =
    this.route.snapshot.paramMap.get('companyId') ?? '';

  constructor() {
    if (this.companyId) {
      this.companyStore.selectCompany(this.companyId);
    }
  }

  protected saveCompany(request: CompanyRequest): void {
    if (!this.canUpdate()) {
      return;
    }

    this.companyStore.updateCompany(this.companyId, request, () => {
      this.companyStore.selectCompany(this.companyId);
    });
  }

  protected toggleCompanyActive(company: CompanyResponse): void {
    if (company.isActive) {
      if (!this.canDeactivate()) {
        return;
      }

      this.companyStore.deactivateCompany(this.companyId, () => {
        this.companyStore.selectCompany(this.companyId);
      });

      return;
    }

    if (!this.canActivate()) {
      return;
    }

    this.companyStore.activateCompany(this.companyId, () => {
      this.companyStore.selectCompany(this.companyId);
    });
  }

  protected canUpdate(): boolean {
    return this.permissionService.hasPermission(Permission.CompaniesUpdate);
  }

  protected canDeactivate(): boolean {
    return this.permissionService.hasPermission(Permission.CompaniesDelete);
  }

  protected canActivate(): boolean {
    return this.permissionService.hasPermission(Permission.CompaniesUpdate);
  }

  protected manageDivision(event: {
    data?: CompanyNamedLookupResponse;
    action: CompanyDetailsActions.Create | CompanyDetailsActions.Edit | CompanyDetailsActions.Toggled;
  }): void {
    this.manageNamedLookup({
      action: event.action,
      item: event.data,
      titleText: 'division',
      fieldLabel: 'Division name',

      onCreate: request =>
        this.companyStore.createDivision(request, () => {}),

      onUpdate: (id, request) =>
        this.companyStore.updateDivision(id, request, () => {}),

      onToggle: item => {
        if (item.isActive) {
          this.companyStore.deactivateDivision(
            item.id,
            this.companyId,
            () => {}
          );
        } else {
          this.companyStore.activateDivision(
            item.id,
            this.companyId,
            () => {}
          );
        }
      },
    });
  }

  protected manageJobTitle(event: {
    data?: CompanyNamedLookupResponse;
    action: CompanyDetailsActions.Create | CompanyDetailsActions.Edit | CompanyDetailsActions.Toggled;
  }): void {
    this.manageNamedLookup({
      action: event.action,
      item: event.data,
      titleText: 'job title',
      fieldLabel: 'Job title name',

      onCreate: request =>
        this.companyStore.createJobTitle(request, () => {}),

      onUpdate: (id, request) =>
        this.companyStore.updateJobTitle(id, request, () => {}),

      onToggle: item => {
        if (item.isActive) {
          this.companyStore.deactivateJobTitle(
            item.id,
            this.companyId,
            () => {}
          );
        } else {
          this.companyStore.activateJobTitle(
            item.id,
            this.companyId,
            () => {}
          );
        }
      },
    });
  }

  protected manageOrganisationalLevel(event: {
    data?: CompanyOrganisationalLevelResponse;
    action: CompanyDetailsActions.Create | CompanyDetailsActions.Edit | CompanyDetailsActions.Toggled;
  }): void {
    if (!this.canUpdate()) {
      return;
    }

    if (event.action === CompanyDetailsActions.Toggled && event.data) {
      if (event.data.isActive) {
        this.companyStore.deactivateOrganisationalLevel(
          event.data.id,
          this.companyId,
          () => {}
        );
      } else {
        this.companyStore.activateOrganisationalLevel(
          event.data.id,
          this.companyId,
          () => {}
        );
      }

      return;
    }

    const dialogRef = this.dialog.open(
      OrganisationalLevelModalComponent,
      {
        width: '400px',
      }
    );

    dialogRef.componentRef?.setInput('companyId', this.companyId);
    dialogRef.componentRef?.setInput(
      'initialValue',
      event.data ?? null
    );

    dialogRef.afterClosed().subscribe(
      (result: CreateCompanyOrganisationalLevelRequest | undefined) => {
        if (!result) {
          return;
        }

        if (event.action === CompanyDetailsActions.Create) {
          this.companyStore.createOrganisationalLevel(
            result,
            () => {}
          );
        }

        if (event.action === CompanyDetailsActions.Edit && event.data) {
          this.companyStore.updateOrganisationalLevel(
            event.data.id,
            result,
            () => {}
          );
        }
      }
    );
  }

  private manageNamedLookup(config: {
    action: CompanyDetailsActions.Create | CompanyDetailsActions.Edit | CompanyDetailsActions.Toggled;
    item?: CompanyNamedLookupResponse;
    titleText: string;
    fieldLabel: string;

    onCreate: (request: CompanyNamedLookupRequest) => void;
    onUpdate: (
      id: string,
      request: CompanyNamedLookupRequest
    ) => void;

    onToggle: (
      item: CompanyNamedLookupResponse
    ) => void;
  }): void {
    if (!this.canUpdate()) {
      return;
    }

    if (config.action === CompanyDetailsActions.Toggled && config.item) {
      config.onToggle(config.item);
      return;
    }

    const dialogRef = this.dialog.open(
      DivisionModalComponent,
      {
        width: '400px',
      }
    );

    dialogRef.componentRef?.setInput(
      'companyId',
      this.companyId
    );

    dialogRef.componentRef?.setInput(
      'initialValue',
      config.item ?? null
    );

    dialogRef.componentRef?.setInput(
      'titleText',
      config.titleText
    );

    dialogRef.componentRef?.setInput(
      'fieldLabel',
      config.fieldLabel
    );

    dialogRef.afterClosed().subscribe(
      (result: CompanyNamedLookupRequest | undefined) => {
        if (!result || !this.isCompanyNamedLookupRequest(result)) {
          return;
        }

        if (config.action === CompanyDetailsActions.Create) {
          config.onCreate(result);
        }

        if (config.action === CompanyDetailsActions.Edit && config.item) {
          config.onUpdate(config.item.id, result);
        }
      }
    );
  }

  private isCompanyNamedLookupRequest(
    value: unknown
  ): value is CompanyNamedLookupRequest {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const candidate =
      value as Partial<CompanyNamedLookupRequest>;

    return (
      typeof candidate.companyId === 'string' &&
      typeof candidate.name === 'string'
    );
  }
}